import { isFunction, noop } from 'lodash-es';

import effectRunnerMap from './effectMap';
import * as errorManager from './errorManager';
import { SIGNAL, TASK, TASK_CANCEL, TERMINATE } from './innerActionSymbol';
import { ABORTED, CANCELLED, DONE, RUNNING } from './taskStatus';
import { deferred, forkQueue, isIterator, isPromise, resolvePromise } from './utils';

// 创建一个新的 task
const newTask = (env, mainTask, meta, isRoot, cont = noop) => {
	let status = RUNNING;
	let taskResult;
	let taskError;
	let deferredEnd = null;
	const cancelledDueToErrorTasks = []; //记录错误信息堆栈
	const queue = forkQueue(
		mainTask,
		() => {
			cancelledDueToErrorTasks.push(...queue.getTasks().map((t) => t.meta.name));
		},
		end,
	);

	function cancel() {
		if (status === RUNNING) {
			status = CANCELLED;
			queue.cancelAll();
			end(TASK_CANCEL, false);
		}
	}
	function end(res, isErr) {
		if (!isErr) {
			if (res === TASK_CANCEL) {
				status = CANCELLED;
			} else if (status === CANCELLED) {
				status = DONE;
			}
			taskResult = res;
			deferredEnd && deferredEnd.resolve(res);
		} else {
			status = ABORTED;
			errorManager.push({ meta, cancelledTasks: cancelledDueToErrorTasks });

			if (task.isRoot) {
				const sagaStack = errorManager.toString();
				errorManager.clear();
				env.onError(res, { sagaStack });
			}
			// TODO -- process error
			taskError = res;
			deferredEnd && deferredEnd.reject(res);
		}
		task.cont(res, isErr);
	}
	function toPromise() {
		if (deferredEnd) {
			return deferredEnd.promise;
		}
		deferredEnd = deferred();
		if (status === ABORTED) {
			deferredEnd.reject(taskError);
		} else if (status !== RUNNING) {
			deferredEnd.resolve(taskResult);
		}
		return deferredEnd.promise;
	}

	const task = {
		[TASK]: true,
		meta,
		isRoot,
		cont,
		//
		queue,
		end,
		cancel,
		toPromise,
		isRunning: () => status === RUNNING,
		isCancelled: () => status === CANCELLED || (status === RUNNING && mainTask.status === CANCELLED),
		isAborted: () => status === ABORTED,
		result: () => taskResult,
		error: () => taskError,
	};

	return task;
};

// 执行任务起点，进行任务分配，生成相应的 task
const taskProcessor = (env, iterator, meta, isRoot, cont) => {
	// 进行 next.cancel 的初始化，在调用了runEffect后这个值会被effect的cancel覆盖
	next.cancel = noop;

	// 进行任务分配，创建相关的任务栈
	const mainTask = {
		status: RUNNING,
		meta,
		cancel() {
			if (mainTask.status === RUNNING) {
				mainTask.status = CANCELLED;
				next(TASK_CANCEL);
			}
		},
	};
	// 创建一个对于当前task的描述
	const task = newTask(env, mainTask, meta, isRoot, cont);
	const executingContext = {
		task,
		digestEffect,
	};

	if (cont) {
		cont.cancel = task.cancel;
	}
	next();

	return task;

	// 主要的 generator 驱动函数，通过它来进行相关的枚举驱动
	function next(arg, isErr) {
		try {
			let res;
			if (isErr) {
				res = iterator.throw(arg);
				errorManager.clear();
			} else if (arg === TASK_CANCEL) {
				// 被调用 task.cancel 方法 - 也可能是一个cancelEffect
				mainTask.status = CANCELLED;
				next.cancel();
				// 如果包含 return 方法，进行调用 - 最终标记到事件结束
				res = isFunction(iterator.return) ? iterator.return(TASK_CANCEL) : { done: true, value: TASK_CANCEL };
			} else if (arg === TERMINATE) {
				// 特殊情况 - channel 在关闭时会被触发
				res = isFunction(iterator.return) ? iterator.return() : { done: true };
			} else {
				// 进行真实事件调用
				res = iterator.next(arg);
			}
			if (!res.done) {
				digestEffect(res.value, next);
			} else {
				// task 完成，结束当前task&通知subTask
				if (mainTask.status !== CANCELLED) {
					mainTask.status = DONE;
				}
				mainTask.cont(res.value);
			}
		} catch (error) {
			if (mainTask.status === CANCELLED) {
				throw error;
			}
			console.error(error);
			mainTask.status = ABORTED;
			mainTask.cont(error, true);
		}
	}

	function digestEffect(effect, callback) {
		// 进行callback的互斥，不能同时被触发done和cancel
		let effectSettled;

		function currentCallback(res, isErr) {
			if (effectSettled) {
				return;
			}
			// 进行事件绑定&触发
			effectSettled = true;
			callback.cancel = noop;

			if (isErr) {
				errorManager.setCrashedEffect(effect);
			}
			callback(res, isErr);
		}
		currentCallback.cancel = noop;
		// 在父级callback绑定当前的effect失败逻辑
		callback.cancel = () => {
			if (effectSettled) {
				return;
			}
			effectSettled = true;
			currentCallback.cancel();
			currentCallback.cancel = noop;
		};

		runEffect(effect, currentCallback);
	}

	function runEffect(effect, currCb) {
		if (isPromise(effect)) {
			resolvePromise(effect, currCb);
		} else if (isIterator(effect)) {
			taskProcessor(env, effect, meta, false, currCb);
		} else if (effect && effect[SIGNAL]) {
			const effectRunner = effectRunnerMap[effect.type];
			effectRunner(env, effect.payload, currCb, executingContext);
		} else {
			currCb(effect);
		}
	}
};

export { taskProcessor };
